import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { tokenStorage } from '@/lib/auth/tokenStorage'
import { PageLoader } from '@/components/shared/PageLoader'
import { ErrorState } from '@/components/shared/ErrorState'
import { useCurrentUser } from '../hooks/useAuth'

/**
 * Wraps protected routes. Redirects to /login if no token.
 * Shows a loader while verifying the user.
 * Shows a server error if the backend is unreachable.
 */
export function ProtectedRoute() {
  const location = useLocation()
  const hasToken = tokenStorage.hasTokens()
  const { isLoading, isError, error, refetch } = useCurrentUser()

  if (!hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (isLoading) {
    return <PageLoader />
  }

  if (isError) {
    // Check if it's a network error (server down) vs auth error (401)
    const isNetworkError = !!(error && typeof error === 'object' && 'code' in error && (error as { code?: string }).code === 'ERR_NETWORK')
    const isServerError = !!(error && typeof error === 'object' && 'response' in error && (error as { response?: { status?: number } }).response?.status && (error as { response: { status: number } }).response.status >= 500)

    if (isNetworkError || isServerError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <ErrorState
            title="Server Unavailable"
            message="Could not connect to the server. Please check if the backend is running and try again."
            onRetry={() => { void refetch() }}
          />
        </div>
      )
    }

    // Auth error — token invalid/expired
    tokenStorage.clearTokens()
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <Outlet />
}
